let _profUserId = 'default';
try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(t => { if (t) _profUserId = t.slice(0, 16); }).catch(() => {}); } catch {}

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, SafeAreaView, StatusBar,
  Alert, ActivityIndicator, Animated, Dimensions, Modal, TextInput, Image,
  RefreshControl, FlatList, Platform, Share, KeyboardAvoidingView, Switch,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';

import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

let AsyncStorage;
try { AsyncStorage = require('@react-native-async-storage/async-storage').default; }
catch { AsyncStorage = { getItem: async () => null, setItem: async () => null, removeItem: async () => null }; }

let Ionicons, MaterialCommunityIcons, FontAwesome5;
try {
  const icons = require('@expo/vector-icons');
  Ionicons = icons.Ionicons;
  MaterialCommunityIcons = icons.MaterialCommunityIcons;
  FontAwesome5 = icons.FontAwesome5;
} catch {
  const F = ({ size = 24, color = '#666', style }) => <View style={[{ width: size, height: size, borderRadius: size / 2, backgroundColor: color, opacity: 0.3 }, style]} />;
  Ionicons = F; MaterialCommunityIcons = F; FontAwesome5 = F;
}

let LinearGradient;
try { LinearGradient = require('expo-linear-gradient').LinearGradient; }
catch { LinearGradient = ({ colors, style, children, ...p }) => <View style={[style, { backgroundColor: colors?.[0] || '#0e4e96' }]} {...p}>{children}</View>; }

const { width } = Dimensions.get('window');

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const apiFetch = async (endpoint, options = {}) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('userToken');
    const res = await fetch(`${API_BASE}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...options,
    });
    if (!res.ok) return { error: true, status: res.status };
    return res.json();
  } catch (e) { return { error: true, message: e.message }; }
};

// ─── Constants ────────────────────────────────────────────────────────────────
const SKILL_CATEGORIES = [
  { key: 'all', label: 'All', icon: 'apps' },
  { key: 'technical', label: 'Technical', icon: 'code-slash' },
  { key: 'soft', label: 'Soft Skills', icon: 'people' },
  { key: 'management', label: 'Management', icon: 'briefcase' },
  { key: 'design', label: 'Design', icon: 'color-palette' },
  { key: 'data', label: 'Data & AI', icon: 'analytics' },
  { key: 'language', label: 'Languages', icon: 'language' },
  { key: 'trade', label: 'Trade/Craft', icon: 'construct' },
  { key: 'certification', label: 'Certifications', icon: 'ribbon' },
];

const PROFICIENCY_LEVELS = [
  { key: 'beginner', label: 'Beginner', color: '#94a3b8', min: 0, max: 25 },
  { key: 'elementary', label: 'Elementary', color: '#60a5fa', min: 26, max: 40 },
  { key: 'intermediate', label: 'Intermediate', color: '#fbbf24', min: 41, max: 60 },
  { key: 'advanced', label: 'Advanced', color: '#f97316', min: 61, max: 80 },
  { key: 'expert', label: 'Expert', color: '#22c55e', min: 81, max: 95 },
  { key: 'master', label: 'Master', color: '#8b5cf6', min: 96, max: 100 },
];

const SORT_OPTIONS = [
  { key: 'name_asc', label: 'Name A-Z' },
  { key: 'name_desc', label: 'Name Z-A' },
  { key: 'level_high', label: 'Highest Level' },
  { key: 'level_low', label: 'Lowest Level' },
  { key: 'recent', label: 'Recently Updated' },
  { key: 'gap', label: 'Biggest Gap to Target' },
];

const SKILL_SUGGESTIONS = {
  technical: ['JavaScript', 'Python', 'React Native', 'Node.js', 'TypeScript', 'SQL', 'AWS', 'Docker', 'Git', 'REST APIs', 'GraphQL', 'C#', 'Java', 'Swift', 'Kotlin', 'Flutter', 'HTML/CSS', 'MongoDB', 'PostgreSQL', 'Redis'],
  soft: ['Communication', 'Leadership', 'Team Collaboration', 'Problem Solving', 'Critical Thinking', 'Time Management', 'Adaptability', 'Emotional Intelligence', 'Negotiation', 'Conflict Resolution', 'Public Speaking', 'Active Listening'],
  management: ['Project Management', 'Agile/Scrum', 'Stakeholder Management', 'Risk Management', 'Budget Management', 'Strategic Planning', 'Team Building', 'Performance Management', 'Change Management', 'Resource Planning'],
  design: ['UI/UX Design', 'Figma', 'Adobe XD', 'Sketch', 'Prototyping', 'User Research', 'Wireframing', 'Visual Design', 'Design Systems', 'Motion Design'],
  data: ['Data Analysis', 'Machine Learning', 'TensorFlow', 'Power BI', 'Tableau', 'R', 'Statistics', 'Data Visualization', 'NLP', 'Deep Learning'],
  language: ['English', 'Spanish', 'French', 'German', 'Mandarin', 'Arabic', 'Portuguese', 'Japanese', 'Hindi', 'Italian'],
  trade: ['Electrical', 'Plumbing', 'Carpentry', 'Welding', 'HVAC', 'Masonry', 'Painting', 'Roofing', 'Landscaping', 'Heavy Equipment'],
  certification: ['PMP', 'AWS Certified', 'Google Cloud', 'CISSP', 'ITIL', 'Six Sigma', 'PRINCE2', 'CompTIA', 'Scrum Master', 'CFA'],
};

const getProficiencyLevel = (level) => PROFICIENCY_LEVELS.find(p => level >= p.min && level <= p.max) || PROFICIENCY_LEVELS[0];

// ─── Component ────────────────────────────────────────────────────────────────
export default function SkillProgressScreen() {
  const navigation = useNavigation();

  // ── State ─────────────────────────────────────────────────────────────────
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('level_high');
  const [viewMode, setViewMode] = useState('cards'); // cards | list | grid
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [editingSkill, setEditingSkill] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [endorsements, setEndorsements] = useState({});
  const [skillGoals, setSkillGoals] = useState({});
  const [learningPaths, setLearningPaths] = useState({});
  const [assessmentHistory, setAssessmentHistory] = useState([]);
  const [showOnlyGaps, setShowOnlyGaps] = useState(false);
  const [saving, setSaving] = useState(false);

  // Add form state
  const [goalDesc, setGoalDesc] = useState('');
  const [goalDeadline, setGoalDeadline] = useState('');
  const [newSkill, setNewSkill] = useState({
    name: '', category: 'technical', proficiency_level: 50, years_experience: 0,
    target_level: 80, notes: '', is_primary: false, verified: false,
  });
  const [suggestions, setSuggestions] = useState([]);

  // Animations
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(30)).current;
  const headerAnim = useRef(new Animated.Value(0)).current;

  // ── Load Data ─────────────────────────────────────────────────────────────
  useFocusEffect(useCallback(() => {
    loadAllData();
  }, []));

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [uid, profile] = await Promise.all([
        AsyncStorage.getItem('user_id').then(id => id || AsyncStorage.getItem('userId')),
        AsyncStorage.getItem('user_profile').then(p => p ? JSON.parse(p) : null),
      ]);
      setUserId(uid);
      setUserProfile(profile);

      await Promise.all([
        loadSkills(uid),
        loadGoals(),
        loadEndorsements(),
        loadAssessmentHistory(),
      ]);

      Animated.parallel([
        Animated.timing(fadeAnim, { toValue: 1, duration: 600, useNativeDriver: true }),
        Animated.timing(slideAnim, { toValue: 0, duration: 500, useNativeDriver: true }),
        Animated.timing(headerAnim, { toValue: 1, duration: 800, useNativeDriver: true }),
      ]).start();
    } catch (e) {
      console.error('SkillProgress load error:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadSkills = async (uid) => {
    try {
      // Try the unified service first (handles auth + multi-key user ID resolution)
      try {
        await ProfileManagementService.bootstrap();
        const svcResult = await ProfileManagementService.getSkillProgress();
        if (svcResult.success && Array.isArray(svcResult.data) && svcResult.data.length > 0) {
          const mapped = svcResult.data.map(s => ({
            id: s.id,
            name: s.skill_name || s.name || '',
            category: categorizeSkill(s.skill_name || s.name || '', s.category),
            proficiency_level: parseInt(s.proficiency_level || s.level || 50),
            years_experience: parseFloat(s.years_experience || s.years || 0),
            target_level: parseInt(s.target_level || 80),
            notes: s.notes || '',
            is_primary: s.is_primary || false,
            verified: s.verified || s.endorsed || false,
            last_used: s.last_used || s.updated_at || new Date().toISOString(),
            created_at: s.created_at || new Date().toISOString(),
            endorsement_count: parseInt(s.endorsement_count || 0),
          }));
          setSkills(mapped);
          await AsyncStorage.setItem(`user_skills_${uid || _profUserId}`, JSON.stringify(mapped));
          return;
        }
      } catch (_) { /* fall through */ }

      // Legacy path: direct apiFetch with AsyncStorage cache fallback
      const cached = await AsyncStorage.getItem(`user_skills_${uid || _profUserId}`);
      let apiSkills = [];

      const res = await apiFetch(`/api/data/user_skills?user_id=${uid || ''}`);
      if (!res.error && Array.isArray(res.data || res)) {
        apiSkills = (res.data || res).map(s => ({
          id: s.id,
          name: s.skill_name || s.name || '',
          category: categorizeSkill(s.skill_name || s.name || '', s.category),
          proficiency_level: parseInt(s.proficiency_level || s.level || 50),
          years_experience: parseFloat(s.years_experience || s.years || 0),
          target_level: parseInt(s.target_level || 80),
          notes: s.notes || '',
          is_primary: s.is_primary || false,
          verified: s.verified || s.endorsed || false,
          last_used: s.last_used || s.updated_at || new Date().toISOString(),
          created_at: s.created_at || new Date().toISOString(),
          endorsement_count: parseInt(s.endorsement_count || 0),
        }));
        await AsyncStorage.setItem(`user_skills_${uid || _profUserId}`, JSON.stringify(apiSkills));
      } else if (cached) {
        apiSkills = JSON.parse(cached);
      }
      setSkills(apiSkills);
    } catch (e) {
      console.error('loadSkills error:', e);
      const cached = await AsyncStorage.getItem(`user_skills_${uid || _profUserId}`);
      if (cached) setSkills(JSON.parse(cached));
    }
  };

  const loadGoals = async () => {
    try {
      const g = await AsyncStorage.getItem(`skill_goals_${_profUserId}`);
      if (g) setSkillGoals(JSON.parse(g));
    } catch {}
  };

  const loadEndorsements = async () => {
    try {
      const e = await AsyncStorage.getItem(`skill_endorsements_${_profUserId}`);
      if (e) setEndorsements(JSON.parse(e));
    } catch {}
  };

  const loadAssessmentHistory = async () => {
    try {
      const a = await AsyncStorage.getItem(`skill_assessments_${_profUserId}`);
      if (a) setAssessmentHistory(JSON.parse(a));
    } catch {}
  };

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
  }, []);

  // ── Categorize ────────────────────────────────────────────────────────────
  const categorizeSkill = (name, existing) => {
    if (existing && existing !== 'other') return existing;
    const n = (name || '').toLowerCase();
    for (const [cat, items] of Object.entries(SKILL_SUGGESTIONS)) {
      if (items.some(s => n.includes(s.toLowerCase()) || s.toLowerCase().includes(n))) return cat;
    }
    return 'technical';
  };

  // ── Computed ──────────────────────────────────────────────────────────────
  const filteredSkills = useMemo(() => {
    let result = [...skills];

    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter(s => s.category === selectedCategory);
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s => String(s.name ?? '').toLowerCase().includes(q) || String(s.category ?? '').toLowerCase().includes(q));
    }

    // Gap filter
    if (showOnlyGaps) {
      result = result.filter(s => (s.target_level || 80) - s.proficiency_level > 10);
    }

    // Sort
    switch (sortBy) {
      case 'name_asc': result.sort((a, b) => a.name.localeCompare(b.name)); break;
      case 'name_desc': result.sort((a, b) => b.name.localeCompare(a.name)); break;
      case 'level_high': result.sort((a, b) => b.proficiency_level - a.proficiency_level); break;
      case 'level_low': result.sort((a, b) => a.proficiency_level - b.proficiency_level); break;
      case 'recent': result.sort((a, b) => new Date(b.last_used || 0) - new Date(a.last_used || 0)); break;
      case 'gap': result.sort((a, b) => ((b.target_level || 80) - b.proficiency_level) - ((a.target_level || 80) - a.proficiency_level)); break;
    }
    return result;
  }, [skills, selectedCategory, searchQuery, sortBy, showOnlyGaps]);

  const stats = useMemo(() => {
    if (!skills.length) return { total: 0, avg: 0, expert: 0, beginner: 0, improving: 0, primary: 0, verified: 0, totalYears: 0, topCategory: 'N/A', gapCount: 0 };
    const avg = Math.round(skills.reduce((s, sk) => s + sk.proficiency_level, 0) / skills.length);
    const expert = skills.filter(s => s.proficiency_level >= 81).length;
    const beginner = skills.filter(s => s.proficiency_level <= 25).length;
    const primary = skills.filter(s => s.is_primary).length;
    const verified = skills.filter(s => s.verified).length;
    const totalYears = skills.reduce((s, sk) => s + (sk.years_experience || 0), 0);
    const gapCount = skills.filter(s => (s.target_level || 80) - s.proficiency_level > 10).length;

    // Top category
    const catCounts = {};
    skills.forEach(s => { catCounts[s.category] = (catCounts[s.category] || 0) + 1; });
    const topCategory = Object.entries(catCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

    return { total: skills.length, avg, expert, beginner, improving: skills.length - expert - beginner, primary, verified, totalYears: totalYears.toFixed(1), topCategory, gapCount };
  }, [skills]);

  const categoryBreakdown = useMemo(() => {
    const counts = {};
    skills.forEach(s => {
      if (!counts[s.category]) counts[s.category] = { count: 0, totalLevel: 0 };
      counts[s.category].count++;
      counts[s.category].totalLevel += s.proficiency_level;
    });
    return Object.entries(counts).map(([cat, data]) => ({
      category: cat,
      count: data.count,
      avgLevel: Math.round(data.totalLevel / data.count),
      label: SKILL_CATEGORIES.find(c => c.key === cat)?.label || cat,
    })).sort((a, b) => b.count - a.count);
  }, [skills]);

  // ── CRUD Operations ───────────────────────────────────────────────────────
  const handleAddSkill = async () => {
    if (!newSkill.name.trim()) { Alert.alert('Error', 'Please enter a skill name'); return; }
    if (skills.some(s => String(s.name ?? '').toLowerCase() === String(newSkill.name ?? '').toLowerCase())) {
      Alert.alert('Duplicate', 'This skill already exists in your profile'); return;
    }

    setSaving(true);
    try {
      const skillData = {
        user_id: userId || '',
        skill_name: newSkill.name.trim(),
        category: newSkill.category,
        proficiency_level: newSkill.proficiency_level,
        years_experience: newSkill.years_experience,
        target_level: newSkill.target_level,
        notes: newSkill.notes,
        is_primary: newSkill.is_primary,
        verified: false,
        last_used: new Date().toISOString(),
      };

      const res = await apiFetch('/api/data/user_skills', {
        method: 'POST',
        body: JSON.stringify(skillData),
      });

      const addedSkill = {
        id: res?.data?.id || res?.id || Date.now(),
        name: skillData.skill_name,
        category: skillData.category,
        proficiency_level: skillData.proficiency_level,
        years_experience: skillData.years_experience,
        target_level: skillData.target_level,
        notes: skillData.notes,
        is_primary: skillData.is_primary,
        verified: false,
        last_used: skillData.last_used,
        created_at: new Date().toISOString(),
        endorsement_count: 0,
      };

      const updated = [...skills, addedSkill];
      setSkills(updated);
      await AsyncStorage.setItem(`user_skills_${userId || _profUserId}`, JSON.stringify(updated));

      setShowAddModal(false);
      setNewSkill({ name: '', category: 'technical', proficiency_level: 50, years_experience: 0, target_level: 80, notes: '', is_primary: false, verified: false });
      setSuggestions([]);
    } catch (e) {
      Alert.alert('Error', 'Failed to add skill. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSkill = async () => {
    if (!editingSkill) return;
    setSaving(true);
    try {
      await apiFetch(`/api/data/user_skills/${editingSkill.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          skill_name: editingSkill.name,
          category: editingSkill.category,
          proficiency_level: editingSkill.proficiency_level,
          years_experience: editingSkill.years_experience,
          target_level: editingSkill.target_level,
          notes: editingSkill.notes,
          is_primary: editingSkill.is_primary,
          last_used: new Date().toISOString(),
        }),
      });

      const updated = skills.map(s => s.id === editingSkill.id ? { ...editingSkill, last_used: new Date().toISOString() } : s);
      setSkills(updated);
      await AsyncStorage.setItem(`user_skills_${userId || _profUserId}`, JSON.stringify(updated));
      setShowEditModal(false);
      setEditingSkill(null);
    } catch (e) {
      Alert.alert('Error', 'Failed to update skill.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSkill = (skill) => {
    Alert.alert('Delete Skill', `Remove "${skill.name}" from your profile?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete', style: 'destructive', onPress: async () => {
          try {
            await apiFetch(`/api/data/user_skills/${skill.id}`, { method: 'DELETE' });
            const updated = skills.filter(s => s.id !== skill.id);
            setSkills(updated);
            await AsyncStorage.setItem(`user_skills_${userId || _profUserId}`, JSON.stringify(updated));
            setShowDetailModal(false);
          } catch (e) {
            Alert.alert('Error', 'Failed to delete skill.');
          }
        },
      },
    ]);
  };

  const handleSetGoal = async (skillId, goal) => {
    const updated = { ...skillGoals, [skillId]: goal };
    setSkillGoals(updated);
    await AsyncStorage.setItem(`skill_goals_${_profUserId}`, JSON.stringify(updated));
    setShowGoalModal(false);
  };

  const handleShareSkills = async () => {
    const topSkills = [...skills].sort((a, b) => b.proficiency_level - a.proficiency_level).slice(0, 5);
    const text = `My Top Skills:\n${topSkills.map((s, i) => `${i + 1}. ${s.name} - ${getProficiencyLevel(s.proficiency_level).label} (${s.proficiency_level}%)`).join('\n')}\n\nTracked on JobEng`;
    try { await Share.share({ message: text, title: 'My Skills' }); } catch {}
  };

  // ── Suggestion handler ────────────────────────────────────────────────────
  const handleSkillNameChange = (text) => {
    setNewSkill(prev => ({ ...prev, name: text }));
    if (text.length >= 2) {
      const catSuggestions = SKILL_SUGGESTIONS[newSkill.category] || [];
      const allSuggestions = Object.values(SKILL_SUGGESTIONS).flat();
      const combined = [...new Set([...catSuggestions, ...allSuggestions])];
      const matched = combined.filter(s => s.toLowerCase().includes(text.toLowerCase()) && !skills.some(sk => String(sk.name ?? '').toLowerCase() === s.toLowerCase()));
      setSuggestions(matched.slice(0, 8));
    } else {
      setSuggestions([]);
    }
  };

  // ── Render Helpers ────────────────────────────────────────────────────────
  const renderProgressRing = (level, size = 56, strokeWidth = 5) => {
    const prof = getProficiencyLevel(level);
    const radius = (size - strokeWidth) / 2;
    return (
      <View style={{ width: size, height: size, justifyContent: 'center', alignItems: 'center' }}>
        <View style={{ width: size, height: size, borderRadius: size / 2, borderWidth: strokeWidth, borderColor: '#e5e7eb', position: 'absolute' }} />
        <View style={{ width: size, height: size, borderRadius: size / 2, borderWidth: strokeWidth, borderColor: prof.color, position: 'absolute', borderLeftColor: level > 75 ? prof.color : 'transparent', borderBottomColor: level > 50 ? prof.color : 'transparent', borderRightColor: level > 25 ? prof.color : 'transparent', transform: [{ rotate: '-45deg' }] }} />
        <Text style={{ fontSize: size * 0.26, fontWeight: '700', color: prof.color }}>{level}%</Text>
      </View>
    );
  };

  const renderProgressBar = (current, target = 100, color = '#0e4e96', height = 8) => (
    <View style={{ height, backgroundColor: '#e5e7eb', borderRadius: height / 2, overflow: 'hidden', position: 'relative' }}>
      <View style={{ height: '100%', width: `${Math.min(current, 100)}%`, backgroundColor: color, borderRadius: height / 2 }} />
      {target < 100 && (
        <View style={{ position: 'absolute', left: `${target}%`, top: -2, width: 2, height: height + 4, backgroundColor: '#1e293b' }} />
      )}
    </View>
  );

  // ── Skill Card ────────────────────────────────────────────────────────────
  const renderSkillCard = (skill) => {
    const prof = getProficiencyLevel(skill.proficiency_level);
    const gap = (skill.target_level || 80) - skill.proficiency_level;
    const goal = skillGoals[skill.id];

    return (
      <TouchableOpacity
        key={skill.id}
        style={[st.skillCard, skill.is_primary && { borderLeftWidth: 4, borderLeftColor: '#0e4e96' }]}
        onPress={() => { setSelectedSkill(skill); setShowDetailModal(true); }}
        activeOpacity={0.7}
      >
        <View style={st.skillCardHeader}>
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <Text style={st.skillName} numberOfLines={1}>{skill.name}</Text>
              {skill.is_primary && <Ionicons name="star" size={14} color="#fbbf24" />}
              {skill.verified && <Ionicons name="checkmark-circle" size={14} color="#22c55e" />}
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 3, gap: 8 }}>
              <Text style={st.skillCatLabel}>{SKILL_CATEGORIES.find(c => c.key === skill.category)?.label || skill.category}</Text>
              {skill.years_experience > 0 && (
                <Text style={st.yearsLabel}>{skill.years_experience}y exp</Text>
              )}
            </View>
          </View>
          {renderProgressRing(skill.proficiency_level, 52, 4)}
        </View>

        {/* Progress bar towards target */}
        <View style={{ marginTop: 10 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text style={{ fontSize: 11, color: '#64748b' }}>{prof.label}</Text>
            <Text style={{ fontSize: 11, color: '#64748b' }}>Target: {skill.target_level || 80}%</Text>
          </View>
          {renderProgressBar(skill.proficiency_level, skill.target_level || 80, prof.color)}
        </View>

        {/* Footer */}
        <View style={st.skillCardFooter}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            {gap > 0 ? (
              <>
                <Ionicons name="trending-up" size={14} color={gap > 20 ? '#f97316' : '#22c55e'} />
                <Text style={{ fontSize: 11, color: gap > 20 ? '#f97316' : '#22c55e' }}>{gap}% to target</Text>
              </>
            ) : (
              <>
                <Ionicons name="checkmark-circle" size={14} color="#22c55e" />
                <Text style={{ fontSize: 11, color: '#22c55e' }}>Target reached</Text>
              </>
            )}
          </View>
          {skill.endorsement_count > 0 && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
              <Ionicons name="thumbs-up" size={12} color="#64748b" />
              <Text style={{ fontSize: 11, color: '#64748b' }}>{skill.endorsement_count}</Text>
            </View>
          )}
          {goal && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
              <Ionicons name="flag" size={12} color="#8b5cf6" />
              <Text style={{ fontSize: 11, color: '#8b5cf6' }}>{goal.deadline || 'Goal set'}</Text>
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  // ── List view item ────────────────────────────────────────────────────────
  const renderSkillListItem = (skill) => {
    const prof = getProficiencyLevel(skill.proficiency_level);
    return (
      <TouchableOpacity
        key={skill.id}
        style={st.listItem}
        onPress={() => { setSelectedSkill(skill); setShowDetailModal(true); }}
      >
        <View style={[st.listDot, { backgroundColor: prof.color }]} />
        <View style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <Text style={st.listName} numberOfLines={1}>{skill.name}</Text>
            {skill.is_primary && <Ionicons name="star" size={12} color="#fbbf24" />}
          </View>
          <View style={{ marginTop: 4 }}>{renderProgressBar(skill.proficiency_level, skill.target_level || 80, prof.color, 5)}</View>
        </View>
        <View style={{ alignItems: 'flex-end', minWidth: 54 }}>
          <Text style={{ fontSize: 14, fontWeight: '700', color: prof.color }}>{skill.proficiency_level}%</Text>
          <Text style={{ fontSize: 10, color: '#94a3b8' }}>{prof.label}</Text>
        </View>
        <Ionicons name="chevron-forward" size={16} color="#cbd5e1" style={{ marginLeft: 4 }} />
      </TouchableOpacity>
    );
  };

  // ── Grid view item ────────────────────────────────────────────────────────
  const renderSkillGridItem = (skill) => {
    const prof = getProficiencyLevel(skill.proficiency_level);
    return (
      <TouchableOpacity
        key={skill.id}
        style={st.gridItem}
        onPress={() => { setSelectedSkill(skill); setShowDetailModal(true); }}
      >
        {renderProgressRing(skill.proficiency_level, 48, 4)}
        <Text style={st.gridName} numberOfLines={2}>{skill.name}</Text>
        <View style={[st.gridBadge, { backgroundColor: prof.color + '20' }]}>
          <Text style={[st.gridBadgeText, { color: prof.color }]}>{prof.label}</Text>
        </View>
      </TouchableOpacity>
    );
  };

  // ── Stats Header ──────────────────────────────────────────────────────────
  const renderStatsHeader = () => (
    <Animated.View style={[st.statsCard, { opacity: headerAnim, transform: [{ translateY: slideAnim }] }]}>
      <View style={st.statsRow}>
        <View style={st.statBig}>
          {renderProgressRing(stats.avg, 72, 5)}
          <Text style={st.statBigLabel}>Avg Level</Text>
        </View>
        <View style={st.statsGrid}>
          {[
            { v: stats.total, l: 'Total', i: 'layers-outline', c: '#0e4e96' },
            { v: stats.expert, l: 'Expert+', i: 'trophy-outline', c: '#22c55e' },
            { v: stats.gapCount, l: 'Gaps', i: 'alert-circle-outline', c: '#f97316' },
            { v: stats.verified, l: 'Verified', i: 'checkmark-circle-outline', c: '#8b5cf6' },
          ].map((s, i) => (
            <View key={i} style={st.statMini}>
              <Ionicons name={s.i} size={16} color={s.c} />
              <Text style={[st.statMiniVal, { color: s.c }]}>{s.v}</Text>
              <Text style={st.statMiniLabel}>{s.l}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Category breakdown bar */}
      {categoryBreakdown.length > 0 && (
        <View style={{ marginTop: 14 }}>
          <Text style={{ fontSize: 12, fontWeight: '600', color: '#64748b', marginBottom: 6 }}>Skill Distribution</Text>
          <View style={{ flexDirection: 'row', height: 8, borderRadius: 4, overflow: 'hidden', backgroundColor: '#e5e7eb' }}>
            {categoryBreakdown.map((cat, i) => {
              const colors = ['#0e4e96', '#22c55e', '#f97316', '#8b5cf6', '#ec4899', '#06b6d4', '#eab308', '#ef4444', '#6366f1'];
              return (
                <View key={cat.category} style={{ flex: cat.count, backgroundColor: colors[i % colors.length] }} />
              );
            })}
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
            {categoryBreakdown.slice(0, 4).map((cat, i) => {
              const colors = ['#0e4e96', '#22c55e', '#f97316', '#8b5cf6'];
              return (
                <View key={cat.category} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: colors[i % colors.length] }} />
                  <Text style={{ fontSize: 10, color: '#64748b' }}>{cat.label} ({cat.count})</Text>
                </View>
              );
            })}
          </View>
        </View>
      )}
    </Animated.View>
  );

  // ── Add Skill Modal ───────────────────────────────────────────────────────
  const renderAddModal = () => (
    <Modal visible={showAddModal} animationType="slide" transparent>
      <View style={st.modalOverlay}>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={st.modalContainer}>
          <View style={st.modalContent}>
            {/* Header */}
            <View style={st.modalHeader}>
              <Text style={st.modalTitle}>Add New Skill</Text>
              <TouchableOpacity onPress={() => { setShowAddModal(false); setSuggestions([]); setNewSkill({ name: '', category: 'technical', proficiency_level: 50, years_experience: 0, target_level: 80, notes: '', is_primary: false, verified: false }); }}>
                <Ionicons name="close" size={24} color="#64748b" />
              </TouchableOpacity>
            </View>

            <ScrollView showsVerticalScrollIndicator={false} style={{ maxHeight: 500 }}>
              {/* Skill Name */}
              <Text style={st.fieldLabel}>Skill Name *</Text>
              <TextInput
                style={st.input}
                value={newSkill.name}
                onChangeText={handleSkillNameChange}
                placeholder="e.g. React Native, Project Management"
                placeholderTextColor="#94a3b8"
              />

              {/* Suggestions */}
              {suggestions.length > 0 && (
                <View style={st.suggestionsBox}>
                  {suggestions.map((s, i) => (
                    <TouchableOpacity key={i} style={st.suggestionItem} onPress={() => { setNewSkill(prev => ({ ...prev, name: s })); setSuggestions([]); }}>
                      <Ionicons name="add-circle-outline" size={16} color="#0e4e96" />
                      <Text style={st.suggestionText}>{s}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}

              {/* Category */}
              <Text style={st.fieldLabel}>Category</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 14 }}>
                {SKILL_CATEGORIES.filter(c => c.key !== 'all').map(cat => (
                  <TouchableOpacity
                    key={cat.key}
                    style={[st.catChip, newSkill.category === cat.key && st.catChipActive]}
                    onPress={() => setNewSkill(prev => ({ ...prev, category: cat.key }))}
                  >
                    <Ionicons name={cat.icon} size={14} color={newSkill.category === cat.key ? '#fff' : '#64748b'} />
                    <Text style={[st.catChipText, newSkill.category === cat.key && { color: '#fff' }]}>{cat.label}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>

              {/* Proficiency Level */}
              <Text style={st.fieldLabel}>Proficiency Level: {newSkill.proficiency_level}%</Text>
              <View style={{ marginBottom: 6 }}>
                <View style={st.sliderTrack}>
                  <View style={[st.sliderFill, { width: `${newSkill.proficiency_level}%`, backgroundColor: getProficiencyLevel(newSkill.proficiency_level).color }]} />
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                  {PROFICIENCY_LEVELS.map(p => (
                    <TouchableOpacity key={p.key} onPress={() => setNewSkill(prev => ({ ...prev, proficiency_level: Math.round((p.min + p.max) / 2) }))}>
                      <Text style={{ fontSize: 9, color: newSkill.proficiency_level >= p.min && newSkill.proficiency_level <= p.max ? p.color : '#cbd5e1', fontWeight: '600' }}>{p.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              <View style={{ flexDirection: 'row', gap: 8, marginBottom: 14 }}>
                {[10, 25, 50, 75, 90].map(v => (
                  <TouchableOpacity key={v} style={[st.levelBtn, newSkill.proficiency_level === v && { backgroundColor: '#0e4e96' }]}
                    onPress={() => setNewSkill(prev => ({ ...prev, proficiency_level: v }))}>
                    <Text style={[st.levelBtnText, newSkill.proficiency_level === v && { color: '#fff' }]}>{v}%</Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* Target Level */}
              <Text style={st.fieldLabel}>Target Level: {newSkill.target_level}%</Text>
              <View style={{ flexDirection: 'row', gap: 8, marginBottom: 14 }}>
                {[60, 70, 80, 90, 100].map(v => (
                  <TouchableOpacity key={v} style={[st.levelBtn, newSkill.target_level === v && { backgroundColor: '#22c55e' }]}
                    onPress={() => setNewSkill(prev => ({ ...prev, target_level: v }))}>
                    <Text style={[st.levelBtnText, newSkill.target_level === v && { color: '#fff' }]}>{v}%</Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* Years of Experience */}
              <Text style={st.fieldLabel}>Years of Experience</Text>
              <View style={{ flexDirection: 'row', gap: 8, marginBottom: 14 }}>
                {[0, 1, 2, 3, 5, 8, 10, 15].map(v => (
                  <TouchableOpacity key={v} style={[st.levelBtn, newSkill.years_experience === v && { backgroundColor: '#0e4e96' }]}
                    onPress={() => setNewSkill(prev => ({ ...prev, years_experience: v }))}>
                    <Text style={[st.levelBtnText, newSkill.years_experience === v && { color: '#fff' }]}>{v}{v >= 15 ? '+' : ''}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* Notes */}
              <Text style={st.fieldLabel}>Notes (optional)</Text>
              <TextInput
                style={[st.input, { height: 70, textAlignVertical: 'top' }]}
                value={newSkill.notes}
                onChangeText={t => setNewSkill(prev => ({ ...prev, notes: t }))}
                placeholder="Where you used this skill, certifications, etc."
                placeholderTextColor="#94a3b8"
                multiline
              />

              {/* Primary skill toggle */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <View>
                  <Text style={st.fieldLabel}>Primary Skill</Text>
                  <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: -8 }}>Highlight on your profile</Text>
                </View>
                <Switch value={newSkill.is_primary} onValueChange={v => setNewSkill(prev => ({ ...prev, is_primary: v }))} trackColor={{ true: '#0e4e96' }} />
              </View>
            </ScrollView>

            {/* Save Button */}
            <TouchableOpacity style={st.saveBtn} onPress={handleAddSkill} disabled={saving}>
              {saving ? <ActivityIndicator color="#fff" size="small" /> : (
                <>
                  <Ionicons name="checkmark-circle" size={20} color="#fff" />
                  <Text style={st.saveBtnText}>Add Skill</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );

  // ── Edit Skill Modal ──────────────────────────────────────────────────────
  const renderEditModal = () => {
    if (!editingSkill) return null;
    const prof = getProficiencyLevel(editingSkill.proficiency_level);
    return (
      <Modal visible={showEditModal} animationType="slide" transparent>
        <View style={st.modalOverlay}>
          <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={st.modalContainer}>
            <View style={st.modalContent}>
              <View style={st.modalHeader}>
                <Text style={st.modalTitle}>Edit: {editingSkill.name}</Text>
                <TouchableOpacity onPress={() => { setShowEditModal(false); setEditingSkill(null); }}>
                  <Ionicons name="close" size={24} color="#64748b" />
                </TouchableOpacity>
              </View>

              <ScrollView showsVerticalScrollIndicator={false} style={{ maxHeight: 440 }}>
                {/* Category */}
                <Text style={st.fieldLabel}>Category</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 14 }}>
                  {SKILL_CATEGORIES.filter(c => c.key !== 'all').map(cat => (
                    <TouchableOpacity key={cat.key}
                      style={[st.catChip, editingSkill.category === cat.key && st.catChipActive]}
                      onPress={() => setEditingSkill(prev => ({ ...prev, category: cat.key }))}>
                      <Ionicons name={cat.icon} size={14} color={editingSkill.category === cat.key ? '#fff' : '#64748b'} />
                      <Text style={[st.catChipText, editingSkill.category === cat.key && { color: '#fff' }]}>{cat.label}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>

                {/* Proficiency */}
                <Text style={st.fieldLabel}>Proficiency: {editingSkill.proficiency_level}% ({prof.label})</Text>
                <View style={{ flexDirection: 'row', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
                  {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(v => (
                    <TouchableOpacity key={v} style={[st.levelBtn, editingSkill.proficiency_level === v && { backgroundColor: getProficiencyLevel(v).color }]}
                      onPress={() => setEditingSkill(prev => ({ ...prev, proficiency_level: v }))}>
                      <Text style={[st.levelBtnText, editingSkill.proficiency_level === v && { color: '#fff' }]}>{v}</Text>
                    </TouchableOpacity>
                  ))}
                </View>

                {/* Target */}
                <Text style={st.fieldLabel}>Target: {editingSkill.target_level}%</Text>
                <View style={{ flexDirection: 'row', gap: 8, marginBottom: 14 }}>
                  {[60, 70, 80, 90, 100].map(v => (
                    <TouchableOpacity key={v} style={[st.levelBtn, editingSkill.target_level === v && { backgroundColor: '#22c55e' }]}
                      onPress={() => setEditingSkill(prev => ({ ...prev, target_level: v }))}>
                      <Text style={[st.levelBtnText, editingSkill.target_level === v && { color: '#fff' }]}>{v}%</Text>
                    </TouchableOpacity>
                  ))}
                </View>

                {/* Years */}
                <Text style={st.fieldLabel}>Years of Experience</Text>
                <View style={{ flexDirection: 'row', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                  {[0, 1, 2, 3, 5, 8, 10, 15].map(v => (
                    <TouchableOpacity key={v} style={[st.levelBtn, editingSkill.years_experience === v && { backgroundColor: '#0e4e96' }]}
                      onPress={() => setEditingSkill(prev => ({ ...prev, years_experience: v }))}>
                      <Text style={[st.levelBtnText, editingSkill.years_experience === v && { color: '#fff' }]}>{v}{v >= 15 ? '+' : ''}</Text>
                    </TouchableOpacity>
                  ))}
                </View>

                {/* Notes */}
                <Text style={st.fieldLabel}>Notes</Text>
                <TextInput style={[st.input, { height: 70, textAlignVertical: 'top' }]}
                  value={editingSkill.notes} onChangeText={t => setEditingSkill(prev => ({ ...prev, notes: t }))}
                  placeholder="Notes about this skill" placeholderTextColor="#94a3b8" multiline />

                {/* Primary toggle */}
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <Text style={st.fieldLabel}>Primary Skill</Text>
                  <Switch value={editingSkill.is_primary} onValueChange={v => setEditingSkill(prev => ({ ...prev, is_primary: v }))} trackColor={{ true: '#0e4e96' }} />
                </View>
              </ScrollView>

              <TouchableOpacity style={st.saveBtn} onPress={handleUpdateSkill} disabled={saving}>
                {saving ? <ActivityIndicator color="#fff" size="small" /> : (
                  <><Ionicons name="checkmark-circle" size={20} color="#fff" /><Text style={st.saveBtnText}>Save Changes</Text></>
                )}
              </TouchableOpacity>
            </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>
    );
  };

  // ── Skill Detail Modal ────────────────────────────────────────────────────
  const renderDetailModal = () => {
    if (!selectedSkill) return null;
    const skill = selectedSkill;
    const prof = getProficiencyLevel(skill.proficiency_level);
    const gap = (skill.target_level || 80) - skill.proficiency_level;
    const goal = skillGoals[skill.id];
    const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return 'N/A'; } };

    return (
      <Modal visible={showDetailModal} animationType="slide" transparent>
        <View style={st.modalOverlay}>
          <View style={[st.modalContent, { maxHeight: '85%' }]}>
            {/* Header with gradient */}
            <LinearGradient colors={[prof.color, prof.color + 'cc']} style={{ borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, alignItems: 'center' }}>
              <TouchableOpacity style={{ position: 'absolute', right: 16, top: 16 }} onPress={() => setShowDetailModal(false)}>
                <Ionicons name="close-circle" size={28} color="#fff" />
              </TouchableOpacity>
              {renderProgressRing(skill.proficiency_level, 80, 6)}
              <Text style={{ fontSize: 22, fontWeight: '700', color: '#fff', marginTop: 8 }}>{skill.name}</Text>
              <Text style={{ fontSize: 14, color: '#ffffffcc', marginTop: 2 }}>{prof.label}</Text>
              <View style={{ flexDirection: 'row', gap: 12, marginTop: 10 }}>
                {skill.is_primary && <View style={st.detailTag}><Ionicons name="star" size={12} color="#fbbf24" /><Text style={st.detailTagText}>Primary</Text></View>}
                {skill.verified && <View style={st.detailTag}><Ionicons name="checkmark-circle" size={12} color="#22c55e" /><Text style={st.detailTagText}>Verified</Text></View>}
                <View style={st.detailTag}><Ionicons name={SKILL_CATEGORIES.find(c => c.key === skill.category)?.icon || 'code-slash'} size={12} color="#fff" /><Text style={st.detailTagText}>{SKILL_CATEGORIES.find(c => c.key === skill.category)?.label || skill.category}</Text></View>
              </View>
            </LinearGradient>

            <ScrollView style={{ padding: 20 }} showsVerticalScrollIndicator={false}>
              {/* Progress to target */}
              <View style={st.detailSection}>
                <Text style={st.detailSectionTitle}>Progress to Target</Text>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
                  <Text style={{ fontSize: 13, color: '#64748b' }}>Current: {skill.proficiency_level}%</Text>
                  <Text style={{ fontSize: 13, color: '#64748b' }}>Target: {skill.target_level || 80}%</Text>
                </View>
                {renderProgressBar(skill.proficiency_level, skill.target_level || 80, prof.color, 10)}
                {gap > 0 ? (
                  <Text style={{ fontSize: 12, color: '#f97316', marginTop: 6 }}>{gap}% remaining to reach your target</Text>
                ) : (
                  <Text style={{ fontSize: 12, color: '#22c55e', marginTop: 6 }}>Target reached! Consider raising it.</Text>
                )}
              </View>

              {/* Details grid */}
              <View style={st.detailSection}>
                <Text style={st.detailSectionTitle}>Details</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                  {[
                    { icon: 'time-outline', label: 'Experience', value: `${skill.years_experience || 0} years` },
                    { icon: 'thumbs-up-outline', label: 'Endorsements', value: `${skill.endorsement_count || 0}` },
                    { icon: 'calendar-outline', label: 'Added', value: fmtDate(skill.created_at) },
                    { icon: 'refresh-outline', label: 'Last Updated', value: fmtDate(skill.last_used) },
                  ].map((d, i) => (
                    <View key={i} style={st.detailGridItem}>
                      <Ionicons name={d.icon} size={18} color="#0e4e96" />
                      <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{d.label}</Text>
                      <Text style={{ fontSize: 13, fontWeight: '600', color: '#1e293b' }}>{d.value}</Text>
                    </View>
                  ))}
                </View>
              </View>

              {/* Notes */}
              {skill.notes ? (
                <View style={st.detailSection}>
                  <Text style={st.detailSectionTitle}>Notes</Text>
                  <Text style={{ fontSize: 13, color: '#475569', lineHeight: 20 }}>{skill.notes}</Text>
                </View>
              ) : null}

              {/* Goal */}
              {goal && (
                <View style={st.detailSection}>
                  <Text style={st.detailSectionTitle}>Goal</Text>
                  <View style={{ backgroundColor: '#f5f3ff', padding: 12, borderRadius: 10, borderLeftWidth: 3, borderLeftColor: '#8b5cf6' }}>
                    <Text style={{ fontSize: 13, color: '#6d28d9', fontWeight: '600' }}>{goal.description || 'Reach target level'}</Text>
                    {goal.deadline && <Text style={{ fontSize: 11, color: '#8b5cf6', marginTop: 4 }}>Deadline: {goal.deadline}</Text>}
                  </View>
                </View>
              )}

              {/* Proficiency scale */}
              <View style={st.detailSection}>
                <Text style={st.detailSectionTitle}>Proficiency Scale</Text>
                {PROFICIENCY_LEVELS.map(p => (
                  <View key={p.key} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6, opacity: skill.proficiency_level >= p.min ? 1 : 0.4 }}>
                    <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: p.color, marginRight: 8 }} />
                    <Text style={{ fontSize: 12, color: '#475569', flex: 1 }}>{p.label}</Text>
                    <Text style={{ fontSize: 11, color: '#94a3b8' }}>{p.min}–{p.max}%</Text>
                    {skill.proficiency_level >= p.min && skill.proficiency_level <= p.max && (
                      <Ionicons name="arrow-back" size={12} color={p.color} style={{ marginLeft: 4 }} />
                    )}
                  </View>
                ))}
              </View>

              {/* Action buttons */}
              <View style={{ flexDirection: 'row', gap: 10, marginTop: 10, marginBottom: 30 }}>
                <TouchableOpacity style={[st.detailActionBtn, { backgroundColor: '#0e4e96' }]} onPress={() => {
                  setShowDetailModal(false);
                  setEditingSkill({ ...skill });
                  setShowEditModal(true);
                }}>
                  <Ionicons name="create-outline" size={18} color="#fff" />
                  <Text style={st.detailActionText}>Edit</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[st.detailActionBtn, { backgroundColor: '#8b5cf6' }]} onPress={() => {
                  setShowDetailModal(false);
                  setShowGoalModal(true);
                }}>
                  <Ionicons name="flag-outline" size={18} color="#fff" />
                  <Text style={st.detailActionText}>Set Goal</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[st.detailActionBtn, { backgroundColor: '#ef4444' }]} onPress={() => handleDeleteSkill(skill)}>
                  <Ionicons name="trash-outline" size={18} color="#fff" />
                  <Text style={st.detailActionText}>Delete</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  // ── Sort Modal ────────────────────────────────────────────────────────────
  const renderSortModal = () => (
    <Modal visible={showSortModal} animationType="fade" transparent>
      <TouchableOpacity style={st.modalOverlay} activeOpacity={1} onPress={() => setShowSortModal(false)}>
        <View style={[st.modalContent, { maxWidth: 300, alignSelf: 'center' }]}>
          <Text style={st.modalTitle}>Sort Skills</Text>
          {SORT_OPTIONS.map(opt => (
            <TouchableOpacity key={opt.key} style={[st.sortItem, sortBy === opt.key && { backgroundColor: '#eff6ff' }]}
              onPress={() => { setSortBy(opt.key); setShowSortModal(false); }}>
              <Text style={[st.sortItemText, sortBy === opt.key && { color: '#0e4e96', fontWeight: '600' }]}>{opt.label}</Text>
              {sortBy === opt.key && <Ionicons name="checkmark" size={18} color="#0e4e96" />}
            </TouchableOpacity>
          ))}
        </View>
      </TouchableOpacity>
    </Modal>
  );

  // ── Goal Modal ────────────────────────────────────────────────────────────
  const renderGoalModal = () => {
    return (
      <Modal visible={showGoalModal} animationType="fade" transparent>
        <View style={st.modalOverlay}>
          <View style={[st.modalContent, { maxWidth: 360, alignSelf: 'center' }]}>
            <View style={st.modalHeader}>
              <Text style={st.modalTitle}>Set Goal for {selectedSkill?.name}</Text>
              <TouchableOpacity onPress={() => setShowGoalModal(false)}>
                <Ionicons name="close" size={24} color="#64748b" />
              </TouchableOpacity>
            </View>
            <Text style={st.fieldLabel}>Goal Description</Text>
            <TextInput style={st.input} value={goalDesc} onChangeText={setGoalDesc} placeholder="e.g. Reach advanced level" placeholderTextColor="#94a3b8" />
            <Text style={st.fieldLabel}>Deadline (optional)</Text>
            <TextInput style={st.input} value={goalDeadline} onChangeText={setGoalDeadline} placeholder="e.g. June 2026" placeholderTextColor="#94a3b8" />
            <TouchableOpacity style={st.saveBtn} onPress={() => { if (selectedSkill) handleSetGoal(selectedSkill.id, { description: goalDesc, deadline: goalDeadline }); }}>
              <Ionicons name="flag" size={20} color="#fff" />
              <Text style={st.saveBtnText}>Save Goal</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    );
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8fafc' }}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        <ActivityIndicator size="large" color="#0e4e96" />
        <Text style={{ marginTop: 12, fontSize: 15, color: '#64748b' }}>Loading your skills...</Text>
      </View>
    );
  }

  // ── Main Render ───────────────────────────────────────────────────────────
  return (
    <View style={st.container}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <LinearGradient colors={['#0e4e96', '#1e7be2']} style={st.header}>
        <View style={st.headerRow}>
          <TouchableOpacity style={st.headerBtn} onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Dashboard')}>
            <Ionicons name="arrow-back" size={22} color="#fff" />
          </TouchableOpacity>
          <View style={{ flex: 1, alignItems: 'center' }}>
            <Text style={st.headerTitle}>Skills & Progress</Text>
            <Text style={st.headerSub}>{stats.total} skills tracked</Text>
          </View>
          <TouchableOpacity style={st.headerBtn} onPress={handleShareSkills}>
            <Ionicons name="share-social-outline" size={20} color="#fff" />
          </TouchableOpacity>
        </View>

        {/* Search bar */}
        <View style={st.searchBar}>
          <Ionicons name="search" size={18} color="#94a3b8" />
          <TextInput
            style={st.searchInput}
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search skills..."
            placeholderTextColor="#94a3b8"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={18} color="#94a3b8" />
            </TouchableOpacity>
          )}
        </View>
      </LinearGradient>

      <ScrollView
        style={{ flex: 1 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} />}
        showsVerticalScrollIndicator={false}
      >
        <Animated.View style={{ opacity: fadeAnim, padding: 16 }}>

          {/* Stats overview */}
          {renderStatsHeader()}

          {/* Category filter chips */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 14, marginHorizontal: -16, paddingHorizontal: 16 }}>
            {SKILL_CATEGORIES.map(cat => {
              const count = cat.key === 'all' ? skills.length : skills.filter(s => s.category === cat.key).length;
              return (
                <TouchableOpacity
                  key={cat.key}
                  style={[st.filterChip, selectedCategory === cat.key && st.filterChipActive]}
                  onPress={() => setSelectedCategory(cat.key)}
                >
                  <Ionicons name={cat.icon} size={14} color={selectedCategory === cat.key ? '#fff' : '#64748b'} />
                  <Text style={[st.filterChipText, selectedCategory === cat.key && { color: '#fff' }]}>
                    {cat.label} {count > 0 ? `(${count})` : ''}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>

          {/* Toolbar: View mode, Sort, Filter */}
          <View style={st.toolbar}>
            <View style={{ flexDirection: 'row', gap: 4 }}>
              {[
                { key: 'cards', icon: 'grid-outline' },
                { key: 'list', icon: 'list-outline' },
                { key: 'grid', icon: 'apps-outline' },
              ].map(v => (
                <TouchableOpacity key={v.key} style={[st.viewBtn, viewMode === v.key && st.viewBtnActive]}
                  onPress={() => setViewMode(v.key)}>
                  <Ionicons name={v.icon} size={18} color={viewMode === v.key ? '#0e4e96' : '#94a3b8'} />
                </TouchableOpacity>
              ))}
            </View>
            <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
              <TouchableOpacity style={st.toolBtn} onPress={() => setShowOnlyGaps(!showOnlyGaps)}>
                <Ionicons name="alert-circle-outline" size={16} color={showOnlyGaps ? '#f97316' : '#64748b'} />
                <Text style={[st.toolBtnText, showOnlyGaps && { color: '#f97316' }]}>Gaps</Text>
              </TouchableOpacity>
              <TouchableOpacity style={st.toolBtn} onPress={() => setShowSortModal(true)}>
                <Ionicons name="swap-vertical" size={16} color="#64748b" />
                <Text style={st.toolBtnText}>Sort</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Skills Section Title */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <Text style={st.sectionTitle}>{filteredSkills.length} Skill{filteredSkills.length !== 1 ? 's' : ''}</Text>
          </View>

          {/* Skills */}
          {filteredSkills.length > 0 ? (
            viewMode === 'grid' ? (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                {filteredSkills.map(renderSkillGridItem)}
              </View>
            ) : viewMode === 'list' ? (
              <View style={st.listContainer}>
                {filteredSkills.map(renderSkillListItem)}
              </View>
            ) : (
              filteredSkills.map(renderSkillCard)
            )
          ) : (
            <View style={st.emptyState}>
              <Ionicons name="school-outline" size={56} color="#cbd5e1" />
              <Text style={st.emptyTitle}>
                {searchQuery ? 'No skills match your search' : selectedCategory !== 'all' ? `No ${SKILL_CATEGORIES.find(c => c.key === selectedCategory)?.label} skills yet` : 'No skills added yet'}
              </Text>
              <Text style={st.emptySubtext}>Add skills to track your professional growth and showcase your expertise</Text>
              <TouchableOpacity style={st.emptyBtn} onPress={() => setShowAddModal(true)}>
                <Ionicons name="add-circle-outline" size={20} color="#fff" />
                <Text style={st.emptyBtnText}>Add Your First Skill</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Quick Actions */}
          <View style={{ marginTop: 20, marginBottom: 10 }}>
            <Text style={st.sectionTitle}>Quick Actions</Text>
          </View>
          <View style={st.actionsGrid}>
            {[
              { label: 'Take Assessment', icon: 'clipboard-outline', color: '#3b82f6', screen: 'SkillAssessment' },
              { label: 'Development Plan', icon: 'rocket-outline', color: '#22c55e', screen: 'SkillDevelopment' },
              { label: 'Find Courses', icon: 'book-outline', color: '#f97316', screen: 'SkillsUpgradeCourses' },
              { label: 'All Skills Library', icon: 'library-outline', color: '#8b5cf6', screen: 'AllSkillsScreen' },
              { label: 'Add Skill', icon: 'add-circle-outline', color: '#0e4e96', action: () => setShowAddModal(true) },
              { label: 'Export Skills', icon: 'download-outline', color: '#06b6d4', action: handleShareSkills },
            ].map((a, i) => (
              <TouchableOpacity key={i} style={st.actionTile}
                onPress={() => a.action ? a.action() : navigation.navigate(a.screen)}>
                <View style={[st.actionIcon, { backgroundColor: a.color + '15' }]}>
                  <Ionicons name={a.icon} size={22} color={a.color} />
                </View>
                <Text style={st.actionLabel}>{a.label}</Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Skill Insights */}
          {skills.length >= 3 && (
            <View style={{ marginTop: 20, marginBottom: 30 }}>
              <Text style={st.sectionTitle}>Insights</Text>
              <View style={st.insightCard}>
                <View style={st.insightRow}>
                  <Ionicons name="bulb-outline" size={20} color="#f59e0b" />
                  <View style={{ flex: 1, marginLeft: 10 }}>
                    <Text style={st.insightTitle}>Strongest Area</Text>
                    <Text style={st.insightValue}>
                      {[...skills].sort((a, b) => b.proficiency_level - a.proficiency_level)[0]?.name || 'N/A'} ({[...skills].sort((a, b) => b.proficiency_level - a.proficiency_level)[0]?.proficiency_level}%)
                    </Text>
                  </View>
                </View>
                <View style={[st.insightRow, { marginTop: 12 }]}>
                  <Ionicons name="trending-up-outline" size={20} color="#22c55e" />
                  <View style={{ flex: 1, marginLeft: 10 }}>
                    <Text style={st.insightTitle}>Most Room to Grow</Text>
                    <Text style={st.insightValue}>
                      {[...skills].sort((a, b) => ((b.target_level || 80) - b.proficiency_level) - ((a.target_level || 80) - a.proficiency_level))[0]?.name || 'N/A'}
                    </Text>
                  </View>
                </View>
                <View style={[st.insightRow, { marginTop: 12 }]}>
                  <Ionicons name="time-outline" size={20} color="#0e4e96" />
                  <View style={{ flex: 1, marginLeft: 10 }}>
                    <Text style={st.insightTitle}>Total Experience</Text>
                    <Text style={st.insightValue}>{stats.totalYears} years across {stats.total} skills</Text>
                  </View>
                </View>
                <View style={[st.insightRow, { marginTop: 12 }]}>
                  <Ionicons name="ribbon-outline" size={20} color="#8b5cf6" />
                  <View style={{ flex: 1, marginLeft: 10 }}>
                    <Text style={st.insightTitle}>Top Category</Text>
                    <Text style={st.insightValue}>{SKILL_CATEGORIES.find(c => c.key === stats.topCategory)?.label || stats.topCategory}</Text>
                  </View>
                </View>
              </View>
            </View>
          )}

          <View style={{ height: 40 }} />
        </Animated.View>
      </ScrollView>

      {/* FAB: Add Skill */}
      <TouchableOpacity style={st.fab} onPress={() => setShowAddModal(true)} activeOpacity={0.8}>
        <LinearGradient colors={['#0e4e96', '#1e7be2']} style={st.fabGrad}>
          <Ionicons name="add" size={28} color="#fff" />
        </LinearGradient>
      </TouchableOpacity>

      {/* Modals */}
      {renderAddModal()}
      {renderEditModal()}
      {renderDetailModal()}
      {renderSortModal()}
      {renderGoalModal()}
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const st = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },

  // Header
  header: { paddingTop: Platform.OS === 'ios' ? 54 : 40, paddingBottom: 16, paddingHorizontal: 16 },
  headerRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  headerBtn: { width: 38, height: 38, borderRadius: 19, backgroundColor: 'rgba(255,255,255,0.18)', justifyContent: 'center', alignItems: 'center' },
  headerTitle: { fontSize: 20, fontWeight: '700', color: '#fff' },
  headerSub: { fontSize: 12, color: '#ffffffaa', marginTop: 1 },
  searchBar: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, paddingHorizontal: 12, height: 42 },
  searchInput: { flex: 1, fontSize: 14, color: '#1e293b', marginLeft: 8 },

  // Stats
  statsCard: { backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 8, elevation: 3 },
  statsRow: { flexDirection: 'row', alignItems: 'center' },
  statBig: { alignItems: 'center', marginRight: 20 },
  statBigLabel: { fontSize: 12, color: '#64748b', marginTop: 4, fontWeight: '500' },
  statsGrid: { flex: 1, flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  statMini: { width: '46%', backgroundColor: '#f8fafc', borderRadius: 10, padding: 8, alignItems: 'center' },
  statMiniVal: { fontSize: 18, fontWeight: '700', marginTop: 2 },
  statMiniLabel: { fontSize: 10, color: '#94a3b8', marginTop: 1 },

  // Filter chips
  filterChip: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: '#fff', borderRadius: 20, paddingHorizontal: 12, paddingVertical: 7, marginRight: 8, borderWidth: 1, borderColor: '#e2e8f0' },
  filterChipActive: { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  filterChipText: { fontSize: 12, color: '#64748b', fontWeight: '500' },

  // Toolbar
  toolbar: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  viewBtn: { width: 34, height: 34, borderRadius: 8, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f1f5f9' },
  viewBtnActive: { backgroundColor: '#dbeafe' },
  toolBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: '#f1f5f9', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6 },
  toolBtnText: { fontSize: 12, color: '#64748b', fontWeight: '500' },

  // Section
  sectionTitle: { fontSize: 16, fontWeight: '700', color: '#1e293b' },

  // Skill card
  skillCard: { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },
  skillCardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  skillName: { fontSize: 15, fontWeight: '600', color: '#1e293b', flex: 1 },
  skillCatLabel: { fontSize: 11, color: '#94a3b8', backgroundColor: '#f1f5f9', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 6 },
  yearsLabel: { fontSize: 11, color: '#64748b' },
  skillCardFooter: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTopWidth: 1, borderTopColor: '#f1f5f9' },

  // List view
  listContainer: { backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },
  listItem: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f1f5f9', gap: 10 },
  listDot: { width: 10, height: 10, borderRadius: 5 },
  listName: { fontSize: 14, fontWeight: '500', color: '#1e293b' },

  // Grid view
  gridItem: { width: (width - 52) / 3, backgroundColor: '#fff', borderRadius: 12, padding: 12, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  gridName: { fontSize: 11, fontWeight: '600', color: '#1e293b', textAlign: 'center', marginTop: 6 },
  gridBadge: { marginTop: 6, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 6 },
  gridBadgeText: { fontSize: 9, fontWeight: '600' },

  // Empty
  emptyState: { alignItems: 'center', paddingVertical: 50 },
  emptyTitle: { fontSize: 17, fontWeight: '600', color: '#64748b', marginTop: 14 },
  emptySubtext: { fontSize: 13, color: '#94a3b8', textAlign: 'center', marginTop: 6, paddingHorizontal: 30 },
  emptyBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#0e4e96', borderRadius: 12, paddingHorizontal: 20, paddingVertical: 12, marginTop: 20 },
  emptyBtnText: { fontSize: 14, fontWeight: '600', color: '#fff' },

  // Actions
  actionsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  actionTile: { width: (width - 52) / 3, backgroundColor: '#fff', borderRadius: 12, padding: 14, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  actionIcon: { width: 44, height: 44, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  actionLabel: { fontSize: 11, fontWeight: '500', color: '#475569', marginTop: 8, textAlign: 'center' },

  // Insights
  insightCard: { backgroundColor: '#fff', borderRadius: 14, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },
  insightRow: { flexDirection: 'row', alignItems: 'center' },
  insightTitle: { fontSize: 12, color: '#94a3b8' },
  insightValue: { fontSize: 14, fontWeight: '600', color: '#1e293b', marginTop: 1 },

  // FAB
  fab: { position: 'absolute', bottom: 24, right: 20, shadowColor: '#0e4e96', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 8, elevation: 8 },
  fabGrad: { width: 56, height: 56, borderRadius: 28, justifyContent: 'center', alignItems: 'center' },

  // Modals
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  modalContainer: { flex: 1, justifyContent: 'flex-end' },
  modalContent: { backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 20, maxHeight: '90%' },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  modalTitle: { fontSize: 18, fontWeight: '700', color: '#1e293b' },

  // Form
  fieldLabel: { fontSize: 13, fontWeight: '600', color: '#475569', marginBottom: 6 },
  input: { backgroundColor: '#f8fafc', borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 10, fontSize: 14, color: '#1e293b', marginBottom: 14 },
  suggestionsBox: { backgroundColor: '#f8fafc', borderRadius: 10, marginBottom: 14, borderWidth: 1, borderColor: '#e2e8f0' },
  suggestionItem: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  suggestionText: { fontSize: 13, color: '#1e293b' },
  catChip: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: '#f1f5f9', borderRadius: 20, paddingHorizontal: 12, paddingVertical: 7, marginRight: 8 },
  catChipActive: { backgroundColor: '#0e4e96' },
  catChipText: { fontSize: 12, color: '#64748b', fontWeight: '500' },
  levelBtn: { backgroundColor: '#f1f5f9', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8, minWidth: 36, alignItems: 'center' },
  levelBtnText: { fontSize: 12, fontWeight: '600', color: '#64748b' },
  sliderTrack: { height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' },
  sliderFill: { height: '100%', borderRadius: 4 },
  saveBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: '#0e4e96', borderRadius: 12, paddingVertical: 14, marginTop: 10 },
  saveBtnText: { fontSize: 15, fontWeight: '600', color: '#fff' },

  // Sort modal
  sortItem: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 14, borderRadius: 8, marginBottom: 4 },
  sortItemText: { fontSize: 14, color: '#475569' },

  // Detail modal
  detailTag: { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 4 },
  detailTagText: { fontSize: 11, color: '#fff', fontWeight: '500' },
  detailSection: { marginBottom: 18 },
  detailSectionTitle: { fontSize: 14, fontWeight: '600', color: '#1e293b', marginBottom: 8 },
  detailGridItem: { width: '47%', backgroundColor: '#f8fafc', borderRadius: 10, padding: 10, alignItems: 'center' },
  detailActionBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 10, paddingVertical: 12 },
  detailActionText: { fontSize: 13, fontWeight: '600', color: '#fff' },
});
